import React, {
  useEffect,
  useId,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { ActionsDropdownProps, DropdownAction } from "./types";
import { firstEnabledFrom, splitDropdownActions } from "./utils";
import {
  CompactTriggerButton,
  DropdownContainer,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem as StyledMenuItem,
  DropdownMenuItemDestructive,
  DropdownSeparator,
  ChevronIcon,
  MenuItemText,
  MenuItemReason,
} from "./styles";
import { useEscapeKey } from "../../hooks";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const DropdownMenuPortal: React.FC<{
  isOpen: boolean;
  menuId: string;
  labelledBy: string;
  menuPosition: { top: number; left: number };
  dropdownRef: React.RefObject<HTMLDivElement>;
  itemRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>;
  regularActions: DropdownAction[];
  destructiveActions: DropdownAction[];
  activeIndex: number;
  onMenuItemClick: (action: DropdownAction) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
}> = ({
  isOpen,
  menuId,
  labelledBy,
  menuPosition,
  dropdownRef,
  itemRefs,
  regularActions,
  destructiveActions,
  activeIndex,
  onMenuItemClick,
  onKeyDown,
}) => {
  const reasonOf = (action: DropdownAction) =>
    action.disabled ? action.disabledReason : undefined;
  const reasonIdOf = (action: DropdownAction) =>
    `${menuId}-${action.id}-reason`;

  const itemProps = (action: DropdownAction, index: number) => ({
    ref: (element: HTMLButtonElement | null) => {
      itemRefs.current[index] = element;
    },
    onClick: () => onMenuItemClick(action),
    role: "menuitem",
    type: "button" as const,
    disabled: action.disabled,
    tabIndex: index === activeIndex ? 0 : -1,
    "aria-describedby": reasonOf(action) ? reasonIdOf(action) : undefined,
  });

  const itemContent = (action: DropdownAction) => {
    const reason = reasonOf(action);
    return (
      <>
        {action.icon && <span aria-hidden="true">{action.icon}</span>}
        {reason ? (
          <MenuItemText>
            {action.label}
            <MenuItemReason id={reasonIdOf(action)} aria-hidden="true">
              {reason}
            </MenuItemReason>
          </MenuItemText>
        ) : (
          action.label
        )}
      </>
    );
  };

  return (
    <div
      ref={dropdownRef}
      onKeyDown={onKeyDown}
      style={{
        position: "fixed",
        top: menuPosition.top,
        left: menuPosition.left,
        zIndex: 10000,
        pointerEvents: "auto",
      }}
    >
      <DropdownMenu
        $isOpen={isOpen}
        id={menuId}
        role="menu"
        aria-labelledby={labelledBy}
      >
        {regularActions.map((action, index) => (
          <StyledMenuItem key={action.id} {...itemProps(action, index)}>
            {itemContent(action)}
          </StyledMenuItem>
        ))}

        {destructiveActions.length > 0 && regularActions.length > 0 && (
          <DropdownSeparator />
        )}

        {destructiveActions.map((action, index) => (
          <DropdownMenuItemDestructive
            key={action.id}
            {...itemProps(action, regularActions.length + index)}
          >
            {itemContent(action)}
          </DropdownMenuItemDestructive>
        ))}
      </DropdownMenu>
    </div>
  );
};

const ActionsDropdown: React.FC<ActionsDropdownProps> = ({
  size = "md",
  dropdownActions,
  testId,
  compact = false,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const baseId = useId();
  const menuId = `${baseId}-menu`;
  const triggerId = `${baseId}-trigger`;

  const {
    regular: regularActions,
    destructive: destructiveActions,
    ordered: orderedActions,
  } = useMemo(() => splitDropdownActions(dropdownActions), [dropdownActions]);

  // Calculate menu position based on button position
  const updateMenuPosition = useCallback(() => {
    if (buttonRef.current && isOpen) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 200;
      const menuHeight = orderedActions.length * 48 + 20;

      // Position to the left of the button, aligned to the right edge
      let left = buttonRect.right - menuWidth;
      let top = buttonRect.bottom + 4;

      // Ensure menu doesn't go off left edge
      if (left < 16) {
        left = buttonRect.left;
      }

      // Ensure menu doesn't go off right edge
      if (left + menuWidth > window.innerWidth - 16) {
        left = window.innerWidth - menuWidth - 16;
      }

      // If menu would go below viewport, show it above the button
      if (top + menuHeight > window.innerHeight - 16) {
        top = buttonRect.top - menuHeight + 8;
      }

      setMenuPosition({ top, left });
    }
  }, [orderedActions, isOpen]);

  // Update position when opening or when button position changes
  useEffect(() => {
    if (isOpen) {
      updateMenuPosition();

      const handleUpdate = () => {
        updateMenuPosition();
      };

      window.addEventListener("scroll", handleUpdate, true);
      window.addEventListener("resize", handleUpdate);

      return () => {
        window.removeEventListener("scroll", handleUpdate, true);
        window.removeEventListener("resize", handleUpdate);
      };
    }
  }, [isOpen, updateMenuPosition]);

  const closeMenu = useCallback((restoreFocus: boolean) => {
    setIsOpen(false);
    setActiveIndex(-1);
    if (restoreFocus) {
      buttonRef.current?.focus();
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // The trigger is outside the portalled menu, so closing on its mousedown would let the
      // following click reopen the menu it just shut.
      if (buttonRef.current?.contains(target)) {
        return;
      }
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        closeMenu(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, closeMenu]);

  const handleEscape = useCallback(() => closeMenu(true), [closeMenu]);

  useEscapeKey(handleEscape, isOpen);

  useEffect(() => {
    if (isOpen && activeIndex >= 0) {
      itemRefs.current[activeIndex]?.focus();
    }
  }, [isOpen, activeIndex]);

  const openMenu = (from: "first" | "last") => {
    setIsOpen(true);
    setActiveIndex(
      from === "first"
        ? firstEnabledFrom(orderedActions, 0, 1)
        : firstEnabledFrom(orderedActions, orderedActions.length - 1, -1),
    );
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOpen) {
      closeMenu(false);
      return;
    }
    openMenu("first");
  };

  const handleButtonKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      openMenu(e.key === "ArrowDown" ? "first" : "last");
    }
  };

  const moveFocusPastTrigger = (reverse: boolean) => {
    const trigger = buttonRef.current;
    if (!trigger) {
      return;
    }

    const pageFocusables = Array.from(
      document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((element) => !dropdownRef.current?.contains(element));
    const triggerIndex = pageFocusables.indexOf(trigger);
    const nextIndex = triggerIndex + (reverse ? -1 : 1);

    pageFocusables[nextIndex]?.focus();
  };

  const handleMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(firstEnabledFrom(orderedActions, activeIndex + 1, 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(firstEnabledFrom(orderedActions, activeIndex - 1, -1));
      return;
    }
    if (e.key === "Home") {
      e.preventDefault();
      setActiveIndex(firstEnabledFrom(orderedActions, 0, 1));
      return;
    }
    if (e.key === "End") {
      e.preventDefault();
      setActiveIndex(
        firstEnabledFrom(orderedActions, orderedActions.length - 1, -1),
      );
      return;
    }
    if (e.key === "Tab") {
      e.preventDefault();
      moveFocusPastTrigger(e.shiftKey);
      closeMenu(false);
    }
  };

  const handleMenuItemClick = (action: DropdownAction) => {
    action.onClick();
    closeMenu(true);
  };

  const renderChevronIcon = () => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="6,9 12,15 18,9" />
    </svg>
  );

  // Create portal container
  const portalContainer = document.body;

  return (
    <>
      <DropdownContainer>
        {compact ? (
          <CompactTriggerButton
            data-testid={testId}
            id={triggerId}
            ref={buttonRef}
            size="small"
            onClick={handleButtonClick}
            onKeyDown={handleButtonKeyDown}
            aria-label="More actions"
            aria-haspopup="menu"
            aria-expanded={isOpen}
            aria-controls={isOpen ? menuId : undefined}
          />
        ) : (
          <DropdownButton
            test-data={testId}
            id={triggerId}
            ref={buttonRef}
            $size={size}
            onClick={handleButtonClick}
            onKeyDown={handleButtonKeyDown}
            title="More actions"
            aria-haspopup="menu"
            aria-expanded={isOpen}
            aria-controls={isOpen ? menuId : undefined}
            type="button"
          >
            Actions
            <ChevronIcon $isOpen={isOpen}>{renderChevronIcon()}</ChevronIcon>
          </DropdownButton>
        )}
      </DropdownContainer>

      {isOpen &&
        createPortal(
          <DropdownMenuPortal
            isOpen={isOpen}
            menuId={menuId}
            labelledBy={triggerId}
            menuPosition={menuPosition}
            dropdownRef={dropdownRef as React.RefObject<HTMLDivElement>}
            itemRefs={itemRefs}
            regularActions={regularActions}
            destructiveActions={destructiveActions}
            activeIndex={activeIndex}
            onMenuItemClick={handleMenuItemClick}
            onKeyDown={handleMenuKeyDown}
          />,
          portalContainer,
        )}
    </>
  );
};

export default ActionsDropdown;
